import {
  BASELINE_WINDOWS,
  type BaselineWindow,
  type DailyBaselines,
  type DailyMetricBaselines,
  type MetricBaseline,
  type MetricKey,
  type NormalizedDailyMetrics,
  type TrendDirection,
} from '@vitalai/health-models';
import { addDays, lastNDateKeys, round } from '@vitalai/shared';

type MetricAccessor = (day: NormalizedDailyMetrics) => number | null;

/**
 * Unico lugar del sistema que sabe como leer una metrica desde
 * NormalizedDailyMetrics. Scoring y Trend engines reutilizan este mapa
 * en lugar de acceder a los campos anidados directamente.
 */
export const METRIC_ACCESSORS: Record<MetricKey, MetricAccessor> = {
  hrv: (day) => day.recovery.hrv,
  restingHeartRate: (day) => day.recovery.restingHeartRate,
  sleepDuration: (day) => day.sleep.totalMinutes,
  deepSleep: (day) => day.sleep.deepMinutes,
  remSleep: (day) => day.sleep.remMinutes,
  sleepEfficiency: (day) => day.sleep.efficiency,
  spo2: (day) => day.recovery.spo2,
  respiratoryRate: (day) => day.recovery.respiratoryRate,
  steps: (day) => day.activity.steps,
  activeCalories: (day) => day.activity.activeCalories,
  exerciseMinutes: (day) => day.activity.exerciseMinutes,
  nutritionCalories: (day) => day.nutrition.calories,
  proteinIntake: (day) => day.nutrition.protein,
  weight: (day) => day.body.weightKg ?? null,
};

export function getMetricValue(day: NormalizedDailyMetrics, metric: MetricKey): number | null {
  return METRIC_ACCESSORS[metric](day);
}

/** Diferencias porcentuales por debajo de este umbral se consideran "estables". */
const STABLE_THRESHOLD_PERCENT = 2;

export function indexByDate(
  history: NormalizedDailyMetrics[],
): Map<string, NormalizedDailyMetrics> {
  return new Map(history.map((day) => [day.date, day]));
}

/**
 * Calcula el baseline de una metrica para una ventana dada, comparando el
 * valor del dia objetivo contra el promedio de los N dias previos (sin
 * incluir el dia objetivo).
 */
export function calculateMetricBaseline(
  history: NormalizedDailyMetrics[],
  targetDateKey: string,
  metric: MetricKey,
  window: BaselineWindow,
  byDate: Map<string, NormalizedDailyMetrics> = indexByDate(history),
): MetricBaseline | null {
  const today = byDate.get(targetDateKey);
  const currentValue = today ? getMetricValue(today, metric) : null;
  if (currentValue === null || currentValue === undefined) return null;

  const previousDayKeys = lastNDateKeys(addDays(targetDateKey, -1), window);
  const values: number[] = [];
  for (const key of previousDayKeys) {
    const day = byDate.get(key);
    if (!day) continue;
    const value = getMetricValue(day, metric);
    if (value !== null && value !== undefined) values.push(value);
  }
  if (values.length === 0) return null;

  const average = values.reduce((sum, value) => sum + value, 0) / values.length;
  if (average === 0) return null;

  const percentDifference = round(((currentValue - average) / average) * 100, 1);
  const trend: TrendDirection =
    Math.abs(percentDifference) < STABLE_THRESHOLD_PERCENT
      ? 'stable'
      : percentDifference > 0
        ? 'up'
        : 'down';

  return {
    metric,
    window,
    current: round(currentValue, 1),
    average: round(average, 1),
    percentDifference,
    trend,
  };
}

/** Calcula, para un dia dado, el baseline de todas las metricas conocidas en las 4 ventanas estandar. */
export function calculateDailyBaselines(
  history: NormalizedDailyMetrics[],
  targetDateKey: string,
): DailyBaselines {
  const byDate = indexByDate(history);
  const metrics: Partial<Record<MetricKey, DailyMetricBaselines>> = {};

  for (const metric of Object.keys(METRIC_ACCESSORS) as MetricKey[]) {
    const windows: DailyMetricBaselines = {};
    for (const window of BASELINE_WINDOWS) {
      const baseline = calculateMetricBaseline(history, targetDateKey, metric, window, byDate);
      if (baseline) windows[window] = baseline;
    }
    if (Object.keys(windows).length > 0) {
      metrics[metric] = windows;
    }
  }

  return { date: targetDateKey, metrics };
}

/**
 * Frase legible para mostrar en UI, ej:
 * "Tu HRV esta 11.6% por encima de tu promedio de 30 dias."
 */
export function describeBaseline(baseline: MetricBaseline, metricLabel: string): string {
  const direction = baseline.percentDifference >= 0 ? 'por encima de' : 'por debajo de';
  const magnitude = Math.abs(baseline.percentDifference);
  return `Tu ${metricLabel} esta ${magnitude}% ${direction} tu promedio de ${baseline.window} dias.`;
}
